/* tslint:disable:max-file-line-count */
import cn from 'classnames';
import {
  Field,
  FieldArray,
  FieldProps,
  Form,
  FormikProps,
  getIn
} from 'formik';
import React, { useEffect } from 'react';
import Checkbox from './Checkbox';
import Debug from './Debug';
import { Props } from './DynamicForm';
import Effects from './Effects';
import File from './File';
import FormArray from './FormArray';
import Input from './Input';
import Radio from './Radio';
import SearchSelect from './SearchSelect';
import Select from './Select';
import Textarea from './Textarea';
import {
  DCheckboxField,
  DCheckboxValue,
  DField,
  DFieldProps,
  DFieldValue,
  DFileField,
  DFormArrayField,
  DFValues,
  DInputField,
  DInputValue,
  DRadioField,
  DRadioValue,
  DSearchSelectField,
  DSelectField,
  DSelectValue,
  DTextAreaField,
  DTextAreaValue
} from './types';
import { isFieldVisible, noop } from './util';

export const mapPropsToValues = (props: Props) =>
  props.data.fields.reduce((values, field) => {
    if (field.type === 'checkbox') {
      const f = field as DCheckboxField;
      if (f.options) {
        values[f.name] = Object.assign(
          {},
          f.options.reduce((val, cur) => {
            return Object.assign({}, val, { [cur.key]: false });
          }, {} as DCheckboxValue),
          f.initialValue
        );
      } else {
        values[f.name] = f.initialValue;
      }
    } else {
      values[field.name] = field.initialValue;
    }
    return values;
  }, {} as DFValues);

export const renderForm = (props: Props & FormikProps<DFValues>) => {
  const {
    data,
    debugInfo,
    onChange,
    validateOnLoad,
    autoComplete,
    modalDOM,
    hideReqMsg,
    reqMsg = 'Required fields are marked with'
  } = props;
  const formClass = cn('clr-form', {
    'clr-form-horizontal': data.orientation === 'horizontal',
    'clr-form-compact': data.orientation === 'compact'
  });
  return (
    <React.Fragment>
      <Form className={formClass} autoComplete={autoComplete}>
        {!hideReqMsg && (
          <p className="crc-form-required-text">
            {reqMsg} <span>*</span>
          </p>
        )}
        {data.sections ? (
          <div
            className={cn(
              'crc-df-sections',
              data.layout ? data.layout.type : 'horizontal'
            )}>
            {data.sections.map((section, i) => (
              <div key={i} className="crc-df-section">
                <h3 className="crc-df-section-title">{section.title}</h3>
                {data.fields
                  .filter(f => section.fields.includes(f.name))
                  .map(renderField(modalDOM))}
              </div>
            ))}
          </div>
        ) : (
          data.fields.map(renderField(modalDOM))
        )}
        <Effects
          validateOnLoad={validateOnLoad !== undefined ? validateOnLoad : true}
          onChange={onChange || noop}
        />
        {debugInfo && <Debug />}
      </Form>
    </React.Fragment>
  );
};

export const renderField = (modalDOM?: HTMLDivElement) => (field: DField) => {
  switch (field.type) {
    case 'select':
      return renderDFField<DSelectField, DSelectValue, HTMLSelectElement>(
        Select,
        field as DSelectField
      );
    case 'checkbox':
    case 'toggle':
      return renderDFField<DCheckboxField, DCheckboxValue, HTMLInputElement>(
        Checkbox,
        field as DCheckboxField
      );
    case 'textarea':
      return renderDFField<DTextAreaField, DTextAreaValue, HTMLTextAreaElement>(
        Textarea,
        field as DTextAreaField
      );
    case 'radio':
      return renderDFField<DRadioField, DRadioValue, HTMLInputElement>(
        Radio,
        field as DRadioField
      );
    case 'array':
      return renderFormArrayField(field as DFormArrayField);
    case 'searchable':
      return renderSearchSelectField(field as DSearchSelectField, modalDOM);
    case 'file':
      return renderFileField(field as DFileField);
    default:
      return renderDFField<DInputField, DInputValue, HTMLInputElement>(
        Input,
        field as DInputField
      );
  }
};

export const renderSearchSelectField = (
  fieldSpec: DSearchSelectField,
  modalDOM?: HTMLDivElement
) => {
  const { name } = fieldSpec;
  return (
    <Field name={name} key={name}>
      {(fieldProps: FieldProps<DFValues>) => {
        return (
          <DFSearchSelect
            fieldProps={fieldProps}
            fieldSpec={fieldSpec}
            modalDOM={modalDOM}
          />
        );
      }}
    </Field>
  );
};

interface DFSearchSelectProps {
  fieldProps: FieldProps<DFValues>;
  fieldSpec: DSearchSelectField;
  modalDOM?: HTMLDivElement;
}

function DFSearchSelect(props: DFSearchSelectProps) {
  const {
    fieldProps: { field, form },
    fieldSpec,
    modalDOM
  } = props;
  const fieldVisible = isFieldVisible(form, fieldSpec.displayRules);

  const { setFieldValue } = form;
  useEffect(() => {
    if (!fieldVisible && fieldSpec.defaultValue !== undefined) {
      setFieldValue(field.name, fieldSpec.defaultValue);
    }
  }, [setFieldValue, field.name, fieldSpec.defaultValue, fieldVisible]);

  return (
    <SearchSelect
      field={fieldSpec}
      hidden={!fieldVisible}
      value={field.value}
      error={getIn(form.errors, field.name)}
      touched={getIn(form.touched, field.name)}
      modalDOM={modalDOM}
      handleChange={field.onChange}
      handleBlur={field.onBlur}
    />
  );
}

export const renderFileField = (fieldSpec: DFileField) => {
  const { name } = fieldSpec;
  return (
    <Field name={name} key={name}>
      {(fieldProps: FieldProps<DFValues>) => {
        return <DFFile fieldProps={fieldProps} fieldSpec={fieldSpec} />;
      }}
    </Field>
  );
};

interface DFFileProps {
  fieldProps: FieldProps<DFValues>;
  fieldSpec: DFileField;
}

function DFFile(props: DFFileProps) {
  const {
    fieldProps: { field, form },
    fieldSpec
  } = props;
  const fieldVisible = isFieldVisible(form, fieldSpec.displayRules);

  const { setFieldValue } = form;

  const onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      if (setFieldValue) {
        setFieldValue(field.name, event.target.files[0]);
      }
    }
  };

  const onReset = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (setFieldValue) {
      setFieldValue(field.name, undefined);
    }
  };

  return (
    <File
      field={fieldSpec}
      hidden={!fieldVisible}
      value={field.value}
      error={getIn(form.errors, field.name)}
      touched={getIn(form.touched, field.name)}
      handleChange={onChange}
      handleBlur={field.onBlur}
      handleReset={onReset}
    />
  );
}

export const renderFormArrayField = (fieldSpec: DFormArrayField) => {
  const { name, defaultValue } = fieldSpec;
  return (
    <FieldArray name={name} key={name}>
      {({ push, remove }) => (
        <Field name={name}>
          {(fieldProps: FieldProps<DFValues>) => {
            const { field, form } = fieldProps;
            const onAddItem = () => push({ ...defaultValue });

            return (
              <FormArray
                field={fieldSpec}
                error={getIn(form.errors, field.name)}
                touched={getIn(form.touched, field.name)}
                onAddItem={onAddItem}
                onRemoveItem={remove}
                value={field.value}
              />
            );
          }}
        </Field>
      )}
    </FieldArray>
  );
};

export const renderDFField = <
  F extends DField,
  V extends DFieldValue,
  E extends HTMLElement
>(
  Component: React.ComponentType<DFieldProps<F, V, E>>,
  fieldSpec: F
) => {
  return (
    <Field name={fieldSpec.name} key={fieldSpec.name}>
      {(fieldProps: FieldProps<DFValues>) => (
        <DFFieldComponent
          fieldProps={fieldProps}
          fieldSpec={fieldSpec}
          Component={Component}
        />
      )}
    </Field>
  );
};

interface FieldComponentProps<
  F extends DField,
  V extends DFieldValue,
  E extends HTMLElement
> {
  Component: React.ComponentType<DFieldProps<F, V, E>>;
  fieldProps: FieldProps<DFValues>;
  fieldSpec: F;
}

function DFFieldComponent<
  F extends DField,
  V extends DFieldValue,
  E extends HTMLElement
>(props: FieldComponentProps<F, V, E>) {
  const {
    fieldProps: { field, form },
    fieldSpec,
    Component
  } = props;

  const fieldVisible = isFieldVisible(form, fieldSpec.displayRules);

  const { setFieldValue } = form;
  useEffect(() => {
    if (!fieldVisible && fieldSpec.defaultValue !== undefined) {
      setFieldValue(field.name, fieldSpec.defaultValue);
    }
  }, [setFieldValue, field.name, fieldSpec.defaultValue, fieldVisible]);

  return (
    <Component
      field={fieldSpec}
      hidden={!fieldVisible}
      error={getIn(form.errors, field.name)}
      touched={getIn(form.touched, field.name)}
      handleBlur={field.onBlur}
      handleChange={field.onChange}
      value={field.value}
    />
  );
}
